import React, { useMemo, useRef } from 'react';
import {
  ExpressionVariables,
  ExpressionVariablesRecord,
  getExpressionVariablesRecord,
  getVariablesById,
} from '@shared/helpers/expression-variables.helpers';
import { omit } from '@shared/helpers/object.helpers';
import { ExpressionValue, IElementID } from '@shared/models/project.model';
import { useProject } from '@client/store/project';
import { useSelectedVersionId } from '@client/store/ui';
import { useMemoDeepEqual } from './use-utils.hooks';
import {
  formatExpressionErrorMessage,
  isEqualExpressionValues,
  fixNumberFormatSolve,
} from '@shared/helpers/expression-solving.helpers';
import { usePvgisEnvironmentHourData } from '@client/store/pvgis';
import { useDataTables } from '@features/data-table/store/data-table-store.hooks';
import { useResolvedVersion } from './use-resolved-version.hooks';
import { useSelectedProposalId } from './use-selected-proposal-id.hooks';
import { getProposalById } from '@shared/helpers/proposal.helpers';
import { getEmptyFrozenArray } from '@shared/helpers/array.helpers';
import { getBuildingDesignFromProjectOrBuilding } from '@shared/helpers/design/design.helpers';
import { getProjectMeta } from '@shared/helpers/project.helpers';
import {
  getStoreySumExpressionVariables,
  emptyStoreySummedValues,
  withPlaceholderStoreyIfEmpty,
  sumGarageGfaFromEnrichedStoreys,
  type IEnrichedStorey,
  type IStoreySum,
  type VersionStoreySum,
} from '@shared/helpers/storeys.helpers';
import { getDesignExpressionVariables } from '@shared/helpers/design/expressions/design-expression-variables.helpers';
import {
  getActivityIdFromDesignExpressionVariables,
  sumStoreysFromDesignExpressionVariables,
} from '@shared/helpers/design/expressions/storey-sum-from-design.helpers';

export function useVersionExpressionVariablesRecord(): ExpressionVariablesRecord {
  const project = useProject();
  const environmentHourData = usePvgisEnvironmentHourData();
  const dataTables = useDataTables();
  const resolvedVersion = useResolvedVersion();
  const selectedProposalId = useSelectedProposalId();
  const projectId = project?.id;
  const projectMeta = project ? getProjectMeta(project) : undefined;
  const projectDesign = project
    ? getBuildingDesignFromProjectOrBuilding(project)
    : undefined;
  const proposal = React.useMemo(
    () =>
      resolvedVersion && selectedProposalId
        ? getProposalById(resolvedVersion, selectedProposalId, false)
        : undefined,
    [resolvedVersion, selectedProposalId],
  );

  return React.useMemo(() => {
    if (!project || !resolvedVersion) return {};
    return getExpressionVariablesRecord(
      project,
      resolvedVersion,
      environmentHourData ?? getEmptyFrozenArray(),
      undefined,
      dataTables,
      { proposal },
    );
    // `project` read from closure; invalidation follows meta/design (same as enrich caches).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- project ref omitted to avoid store churn
  }, [
    projectId,
    projectMeta,
    projectDesign,
    environmentHourData,
    dataTables,
    resolvedVersion,
    proposal,
  ]);
}

/**
 * Get available variables for a specific element
 * @param id
 * @returns
 */
export const useElementExpressionVariablesById = (
  id: IElementID | undefined,
  ...excludeKeys: (string | undefined)[]
): ExpressionVariables => {
  const exclude = useMemoDeepEqual(() => excludeKeys, excludeKeys);
  const record = useVersionExpressionVariablesRecord();
  return React.useMemo(
    () => omit(getVariablesById(record, id), ...exclude) as ExpressionVariables,
    [record, id, exclude],
  );
};

/**
 * Get available variables for the selected version
 */
export const useVersionVariables = (): ExpressionVariables => {
  const record = useVersionExpressionVariablesRecord();
  const versionId = useSelectedVersionId();
  return React.useMemo(
    () => getVariablesById(record, versionId),
    [record, versionId],
  );
};

const emptyVersionStoreySum = (): VersionStoreySum => ({
  ...emptyStoreySummedValues,
  garage_gfa: 0,
});

/**
 * Attach garage GFA from per-storey {@link IEnrichedStorey} onto the building {@link IStoreySum}.
 */
const toVersionStoreySum = (
  storeys: IEnrichedStorey[],
  sum: IStoreySum,
): VersionStoreySum => ({
  ...sum,
  garage_gfa: sumGarageGfaFromEnrichedStoreys(storeys),
});

/**
 * Legacy {@link VersionStoreySum} for the active version (charts). Not part of {@link defaultVariables}.
 */
export const useVersionStoreySum = (): VersionStoreySum => {
  const project = useProject();
  const versionId = useSelectedVersionId();
  const projectId = project?.id;
  const projectMeta = project ? getProjectMeta(project) : undefined;
  const projectDesign = project
    ? getBuildingDesignFromProjectOrBuilding(project)
    : undefined;

  return React.useMemo(() => {
    if (!projectId || !versionId) return emptyVersionStoreySum();

    if (projectDesign) {
      const designVariables = getDesignExpressionVariables(projectDesign);
      const { storeys, sum } = sumStoreysFromDesignExpressionVariables(
        designVariables,
        {
          activityId:
            getActivityIdFromDesignExpressionVariables(designVariables),
        },
      );
      return toVersionStoreySum(storeys, sum);
    }

    if (!projectMeta) return emptyVersionStoreySum();

    const { storeys, sum } = getStoreySumExpressionVariables(
      withPlaceholderStoreyIfEmpty(projectMeta),
    );
    return toVersionStoreySum(storeys, sum);
  }, [projectId, projectMeta, projectDesign, versionId]);
};

interface IUseSolveExpressionOptions {
  min?: number;
  max?: number;
  variables?: ExpressionVariables;
}

/**
 * Resolve an expression and return a new ExpressionValue
 * If error occurs, return the error object
 * @param expression
 * @param options
 * @returns
 */
export const useSolveExpression = (
  value: ExpressionValue['value'],
  expression: ExpressionValue['expression'] = '',
  { min, max, variables }: IUseSolveExpressionOptions = {},
): ExpressionValue | Error => {
  const lastExpressionValueRef = useRef<ExpressionValue | undefined>(undefined);

  return useMemo(() => {
    try {
      const newExpressionValue = fixNumberFormatSolve(
        value,
        expression,
        variables,
        { min, max },
      );

      // Use latest expression value provided if not changed to not trigger re-render
      if (
        !isEqualExpressionValues(
          lastExpressionValueRef.current,
          newExpressionValue,
          true,
        )
      ) {
        lastExpressionValueRef.current = newExpressionValue;
      }
      return lastExpressionValueRef.current as ExpressionValue;
    } catch (e) {
      const error = e as Error;
      error.message = formatExpressionErrorMessage(error);

      return error;
    }
  }, [value, expression, max, min, variables]);
};

export const useSolveExpressionError = (
  value: ExpressionValue['value'],
  expression: ExpressionValue['expression'] = '',
  options?: IUseSolveExpressionOptions,
): Error | undefined => {
  const solved = useSolveExpression(value, expression, options);
  return solved instanceof Error ? solved : undefined;
};
